import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  HiOutlineArrowPath,
  HiOutlineInformationCircle,
  HiOutlinePencilSquare,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineNoSymbol,
  HiOutlineMagnifyingGlass,
  HiOutlineMapPin,
  HiOutlineChatBubbleLeft,
  HiOutlineXMark,
  HiOutlineClipboardDocumentCheck,
} from "react-icons/hi2";
import { ChevronDown, Search, X } from 'lucide-react'

/* --- helpers --- */
const statusBadge = (status) => {
  const map = { Pendiente: "badge-yellow", Activo: "badge-green", Apartado: "badge-blue", Extendido: "badge-blue", Finalizado: "badge-gray", Rechazado: "badge-red", Cancelado: "badge-red" };
  return map[status] || "badge-gray";
};

const toLocalDateFromUTCDateOnly = (iso) => {
  if (!iso) return null;
  const [datePart] = iso.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatFechaCortaMX = (iso) => {
  const d = toLocalDateFromUTCDateOnly(iso);
  return d?.toLocaleDateString("es-MX", { weekday: "short", day: "2-digit", month: "short" }) || "\u2014";
};

const formatFecha = (fechaISO) => {
  const fechaSinHora = fechaISO.split("T")[0];
  const [anio, mes, dia] = fechaSinHora.split("-");
  const meses = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  return `${parseInt(dia, 10)} de ${meses[parseInt(mes, 10) - 1]}, ${anio}`;
};

const formatHora = (hora) => {
  const [hh, mm] = hora.split(":");
  const horaInt = parseInt(hh, 10);
  const periodo = horaInt >= 12 ? "PM" : "AM";
  const hora12 = horaInt % 12 || 12;
  return `${hora12}:${mm} ${periodo}`;
};

const getPrimerDiaItinerario = (itinerario = []) => {
  if (!Array.isArray(itinerario) || itinerario.length === 0) return null;
  const sorted = [...itinerario].sort((a, b) => toLocalDateFromUTCDateOnly(a.fecha) - toLocalDateFromUTCDateOnly(b.fecha));
  return sorted[0];
};

const getGoogleMapsSearchUrl = (address) => {
  if (!address) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};

const getGoogleMapsEmbedUrl = (address) => {
  if (!address) return "";
  return `https://maps.google.com/maps?q=${encodeURIComponent(address)}&output=embed&z=14`;
};

function SearchableUserSelect({ users, value, onChange, label, placeholder, required }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef(null)
  const inputRef = useRef(null)

  const isSelectedUser = (u) => u.id === value || u.correo_tecnico === value
  const selectedUser = users.find(isSelectedUser)

  const filtered = search.trim()
    ? users.filter((u) => {
      const q = search.toLowerCase()
      return (
        u.nombre_tecnico?.toLowerCase().includes(q) ||
        u.sucursal?.toLowerCase().includes(q)
      )
    })
    : users

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (userId) => {
    onChange(userId)
    console.log("Selected user ID:", userId);
    setSearch('')
    setOpen(false)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onChange('')
    setSearch('')
  }

  return (
    <div ref={containerRef} className="relative w-full">
      {label && <span className="mb-1.5 block text-sm font-medium text-[color:var(--text-secondary)]">{label}</span>}

      {/* Hidden native input for form validation */}
      {required && (
        <input
          tabIndex={-1}
          autoComplete="off"
          style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
          value={value || ''}
          required
          onChange={() => { }}
        />
      )}

      {/* Trigger */}
      <button
        type="button"
        onClick={() => {
          setOpen((prev) => !prev)
          setTimeout(() => inputRef.current?.focus(), 0)
        }}
        className="flex w-full items-center justify-between rounded-xl border border-slate-300 border-[color:var(--input-border)] bg-white bg-[color:var(--input-bg)] px-3 py-2.5 text-left text-sm text-slate-800 shadow-sm outline-none ring-orange-200 transition focus:ring"
      >
        {selectedUser ? (
          <span className="truncate text-[color:var(--text-primary)]">
            {selectedUser.nombre_tecnico}
          </span>
        ) : (
          <span className="text-[color:var(--text-muted)]">{placeholder || 'Seleccionar…'}</span>
        )}
        <span className="flex shrink-0 items-center gap-1 text-[color:var(--text-muted)]">
          {selectedUser && (
            <X
              size={14}
              className="cursor-pointer hover:text-rose-500"
              onClick={handleClear}
            />
          )}
          <ChevronDown size={14} className={`transition ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-[60] mt-1 w-full overflow-hidden rounded-xl border border-slate-200 border-[color:var(--surface-border)] bg-white bg-[color:var(--surface-elevated)] shadow-xl shadow-slate-200/70 shadow-[color:var(--surface-shadow)]">
          {/* Search input */}
          <div className="flex items-center gap-2 border-b border-slate-200 border-[color:var(--surface-border)] bg-white px-3 py-2">
            <Search size={14} className="shrink-0 text-[color:var(--text-muted)]" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o área…"
              className="w-full bg-transparent text-sm text-slate-800 text-[color:var(--text-primary)] outline-none placeholder:text-slate-400 placeholder:text-[color:var(--text-muted)]"
            />
            {search && (
              <X
                size={14}
                className="shrink-0 cursor-pointer text-[color:var(--text-muted)] hover:text-rose-500"
                onClick={() => setSearch('')}
              />
            )}
          </div>

          {/* Options list */}
          <ul className="max-h-56 overflow-y-auto bg-white py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-3 text-center text-xs text-[color:var(--text-muted)]">
                Sin resultados
              </li>
            ) : (
              filtered.map((user) => (
                <li
                  key={user.id}
                  onClick={() => handleSelect(user.id)}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition hover:bg-[color:var(--sidebar-hover-bg)] ${isSelectedUser(user) ? 'bg-[color:var(--sidebar-active-bg)]/10 font-medium text-[color:var(--btn-primary-bg)]' : 'text-[color:var(--text-primary)]'
                    }`}
                >
                  <span className="truncate">{user.nombre_tecnico}</span>
                  <span className="ml-auto shrink-0 truncate text-xs text-[color:var(--text-muted)]" style={{ maxWidth: '45%' }}>{user.sucursal}</span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

/* --- Component --- */
const AgendarServicio = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [reservas, setReservas] = useState([]);
  const [reservasActivas, setReservasActivas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingActivas, setLoadingActivas] = useState(true);
  const [loadingPendientes, setLoadingPendientes] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReserva, setSelectedReserva] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [selectedTecnico, setSelectedTecnico] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedTipoServicio, setSelectedTipoServicio] = useState("");
  const [selectedReservaModal, setSelectedReservaModal] = useState(null);
  const [open, setOpen] = useState(false);
  const [tecnicos, setTecnicos] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [searchTicket, setSearchTicket] = useState("");
  const [searchTicketPendientes, setSearchTicketPendientes] = useState("");
  const [loadingAsignar, setLoadingAsignar] = useState(false);

  const [apartadosSinTecnico, setApartadosSinTecnico] = useState([]);
  const [loadingApartados, setLoadingApartados] = useState(true);
  const [openAsignarApartado, setOpenAsignarApartado] = useState(false);
  const [selectedApartado, setSelectedApartado] = useState(null);
  const [tecnicoParaApartado, setTecnicoParaApartado] = useState("");
  const [loadingAsignarApartado, setLoadingAsignarApartado] = useState(false);
  const [eliminandoApartadoId, setEliminandoApartadoId] = useState(null);

  /* --- Fetchers --- */
  const fetchReservas = async () => {
    setLoading(true);
    setError(null);
    fetchReservasActivas();
    try {
      const response = await axios.get(`https://desarrollotecnologicoar.com/api5/reservas_estatus/Pendiente`);
      setReservas(response.data);
      setLoadingPendientes(false);
    } catch (err) {
      setError("Error al obtener las reservas.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReservasActivas = async () => {
    setLoadingActivas(true);
    setSearchTicket("");
    try {
      const [activosRes, extendidosRes] = await Promise.all([
        axios.get(`https://desarrollotecnologicoar.com/api5/reservas_estatus/Activo`),
        axios.get(`https://desarrollotecnologicoar.com/api5/reservas_estatus/Extendido`),
      ]);
      setReservasActivas([...activosRes.data, ...extendidosRes.data]);
    } catch (error) {
      console.error("Error al obtener reservas activas:", error);
    }
    setLoadingActivas(false);
  };

  const fetchReservasPendientes = async () => {
    setLoadingPendientes(true);
    try {
      const response = await axios.get(`https://desarrollotecnologicoar.com/api5/reservas_estatus/Pendiente`);
      setReservas(response.data);
    } catch (error) {
      console.error("Error al obtener reservas pendientes:", error);
    }
    setLoadingPendientes(false);
  };

  const fetchTecnicos = async () => {
    try {
      const response = await axios.get(`https://desarrollotecnologicoar.com/api5/tecnicos`);
      setTecnicos(response.data);
    } catch (err) {
      console.error("Error al obtener los tecnicos:", err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`https://desarrollotecnologicoar.com/api10/templates`);
      setTemplates(response.data);
    } catch (err) {
      console.error("Error al obtener los templates:", err);
    }
  };

  const fetchApartadosSinTecnico = async () => {
    setLoadingApartados(true);
    try {
      const res = await axios.get(`https://desarrollotecnologicoar.com/api5/reservas_estatus/Apartado`);
      setApartadosSinTecnico(res.data.filter((r) => !r.correo_tecnico || r.correo_tecnico.trim() === ""));
    } catch (err) {
      console.error("Error al obtener apartados sin tecnico:", err);
    } finally {
      setLoadingApartados(false);
    }
  };

  useEffect(() => {
    fetchReservas();
    fetchTecnicos();
    fetchTemplates();
    fetchApartadosSinTecnico();
  }, []);

  /* --- Handlers --- */
  const handleOpenDrawer = (reserva) => { setSelectedReserva(reserva); setDrawerOpen(true); };
  const handleCloseDrawer = () => setDrawerOpen(false);
  const handleOpenModal = (reserva) => { setSelectedReservaModal(reserva); setOpen(true); };
  const handleCloseModal = () => { setSelectedReserva(null); setSelectedTecnico(""); setOpen(false); };
  const handleEditar = (idSelect, method) => navigate(`/editar_solicitud/${idSelect}/${method}`);

  const handleAccept = async () => {
    if (loadingAsignar) return;
    if (!selectedReservaModal || !selectedTecnico || !selectedTipoServicio) {
      alert("Por favor, selecciona un tecnico y un tipo de servicio.");
      return;
    }
    if (!selectedReservaModal.itinerario || selectedReservaModal.itinerario.length === 0) {
      alert("Esta reserva no tiene itinerario. Por favor, agrega un itinerario antes de aprobar el servicio.");
      return;
    }
    setLoadingAsignar(true);
    const updatedReserva = { ...selectedReservaModal };
    updatedReserva.actividades = updatedReserva.actividades.map((actividad) => ({ nombre_actividad: actividad }));
    var datosTecnico = tecnicos.find((tec) => tec.correo_tecnico === selectedTecnico) || { nombre_tecnico: "Desconocido" };

    const urlGmail = `https://script.google.com/macros/s/AKfycbyZgTCQclnayBnrANVrYJHHJlMbKCLJP378v8eiWpoP96wO7MJxS2FEnyVQ4NRZTid0/exec?tipo=servicio&tipoCorreo=aprobacion&ticket=${encodeURIComponent(updatedReserva.ticket)}&empresa=${encodeURIComponent(updatedReserva.empresa)}&equipo=${encodeURIComponent(updatedReserva.equipo)}&domicilio=${encodeURIComponent(updatedReserva.domicilio)}&nombre=${encodeURIComponent(JSON.stringify({ agente: updatedReserva.nombre_agente, tecnico: datosTecnico.nombre_tecnico }))}&destinatario=${encodeURIComponent(JSON.stringify({ agente: updatedReserva.correo_agente, tecnico: selectedTecnico }))}&actividades=${encodeURIComponent(JSON.stringify(updatedReserva.actividades))}&itinerario=${encodeURIComponent(JSON.stringify(updatedReserva.itinerario))}`;
    const cleanedUrl = urlGmail.replace(/\s+/g, "");

    try {
      await axios.put(`https://desarrollotecnologicoar.com/api5/aceptar_reserva/${selectedReservaModal.id}`, { status: "Activo", correo_tecnico: selectedTecnico, nombre_tecnico_asignado: datosTecnico.nombre_tecnico });
      try {
        await axios.put(`https://desarrollotecnologicoar.com/api5/actualizar_tipo_servicio/${selectedReservaModal.id}`, { tipo_servicio: selectedTipoServicio });
      } catch (error) {
        console.error("Error al actualizar el tipo de servicio:", error);
        alert("Error al actualizar el tipo de servicio.");
      }
      await axios.post("https://desarrollotecnologicoar.com/api5/bandeja_entrada", {
        solicitado: selectedReservaModal.nombre_agente,
        aprobado: user.displayName,
        correo_remitente: selectedTecnico,
        reserva_id: selectedReservaModal.id,
        asunto: `Reserva aprobada para ${selectedReservaModal.empresa}, Ticket #${selectedReservaModal.ticket}`,
      });
      const now = new Date().toISOString();
      await axios.post("https://desarrollotecnologicoar.com/api10/work-orders/from-template", {
        id_reserva: selectedReservaModal.id,
        template_id: selectedTemplate,
        customer_name: updatedReserva.empresa,
        site_address: updatedReserva.domicilio,
        assigned_tech_email: selectedTecnico,
        scheduled_at: now,
      });
      await axios.get(cleanedUrl);
      let notificacionExitosa = true;
      let mensajeErrorNotificacion = "";
      try {
        await axios.post("https://desarrollotecnologicoar.com/api7/notificacion_istp", { correoTecnico: selectedTecnico, ticket: selectedReservaModal.ticket });
      } catch (error) {
        notificacionExitosa = false;
        mensajeErrorNotificacion = error.response?.data?.error || "Error desconocido al enviar la notificacion.";
        console.warn("No se pudo enviar la notificacion:", mensajeErrorNotificacion);
      }
      fetchReservas();
      handleCloseModal();
      if (notificacionExitosa) {
        alert("Reserva aceptada, guardada en la bandeja de entrada y notificacion enviada correctamente.");
      } else {
        alert(`Reserva aceptada y guardada, pero no se pudo enviar la notificacion. Motivo: ${mensajeErrorNotificacion}`);
      }
    } catch (err) {
      console.error("Error al aceptar la reserva:", err);
      alert("Hubo un error al aceptar la reserva.");
    } finally {
      setLoadingAsignar(false);
    }
  };

  const handleReject = async (reservaId) => {
    try {
      await axios.put(`https://desarrollotecnologicoar.com/api5/rechazar_reserva/${reservaId}`, { status: "Rechazado" });
      fetchReservas();
    } catch (err) {
      console.error("Error al rechazar la reserva:", err);
    }
  };

  const handleCancel = async (reservaId, reserva) => {
    try {
      await axios.put(`https://desarrollotecnologicoar.com/api5/cancelar_reserva/${reservaId}`, { status: "Cancelado" }, { headers: { "Content-Type": "application/json" } });
      const emailPayload = {
        ticket: reserva.ticket || "",
        empresa: reserva.empresa || "",
        correo_agente: reserva.correo_agente || reserva.correo_cliente || "",
        agente_edito: user ? user.displayName || user.email : "",
        status: "Rechazado",
      };
      await fetch("https://script.google.com/macros/s/AKfycbyOFiQFm0Bm8UjHFBDEYI5bQGRV60VLo-i8DHB8Wx_FjixH-qV2v-p5DPQQFUcNVMLm/exec", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(emailPayload),
        mode: "no-cors",
      });
      fetchReservas();
    } catch (error) {
      console.error("Error al cancelar la reserva:", error);
      alert("Ocurrio un error al cancelar la reserva");
    }
  };

  const handleAsignarTecnicoApartado = async () => {
    if (!tecnicoParaApartado) { alert("Selecciona un tecnico."); return; }
    if (loadingAsignarApartado) return;
    setLoadingAsignarApartado(true);
    const datosTecnico = tecnicos.find((tec) => tec.correo_tecnico === tecnicoParaApartado) || { nombre_tecnico: "Desconocido" };
    try {
      await axios.put(`https://desarrollotecnologicoar.com/api5/aceptar_reserva/${selectedApartado.id}`, {
        status: "Apartado",
        correo_tecnico: tecnicoParaApartado,
        nombre_tecnico_asignado: datosTecnico.nombre_tecnico,
      });
      fetchApartadosSinTecnico();
      setOpenAsignarApartado(false);
      setSelectedApartado(null);
      setTecnicoParaApartado("");
      alert("Tecnico asignado correctamente. La reserva sigue en estado Apartado hasta que el solicitante complete los datos del ticket.");
    } catch (err) {
      console.error("Error al asignar tecnico:", err);
      alert("Error al asignar el tecnico.");
    } finally {
      setLoadingAsignarApartado(false);
    }
  };

  const handleEliminarApartado = async (ap) => {
    if (eliminandoApartadoId) return;
    const confirmado = window.confirm(`¿Eliminar por completo el apartado de "${ap.empresa}"? Esta accion no se puede deshacer.`);
    if (!confirmado) return;
    setEliminandoApartadoId(ap.id);
    try {
      await axios.delete(`https://desarrollotecnologicoar.com/api5/reservas_apartado_sin_ticket/${ap.id}`);
      setApartadosSinTecnico((prev) => prev.filter((r) => r.id !== ap.id));
      alert("Apartado eliminado correctamente.");
    } catch (err) {
      console.error("Error al eliminar el apartado:", err);
      alert(err.response?.data?.error || "No se pudo eliminar el apartado.");
    } finally {
      setEliminandoApartadoId(null);
    }
  };

  const filteredReservasActivas = reservasActivas.filter((r) => r.ticket.toString().includes(searchTicket));
  const filteredReservasPendientes = reservas.filter((r) => searchTicketPendientes === "" || r.ticket?.toString().includes(searchTicketPendientes));
  const tecnicoEncontrado = tecnicos.find((tec) => tec.correo_tecnico === selectedReserva?.correo_tecnico);

  /* --- Render --- */
  return (
    <div className="page-container">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="page-title">Agendar Servicio</h1>
          <p className="text-sm text-slate-500 mt-1">Aprueba, rechaza o agenda solicitudes de servicio</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchReservasPendientes} disabled={loadingPendientes} className="btn-secondary">
            <HiOutlineArrowPath className={`w-4 h-4 ${loadingPendientes ? "animate-spin" : ""}`} />
            Recargar
          </button>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="xl:col-span-2 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <h2 className="section-title">Solicitudes Pendientes</h2>
            <div className="relative w-full sm:w-64">
              <HiOutlineMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" placeholder="Buscar por ticket..." value={searchTicketPendientes} onChange={(e) => setSearchTicketPendientes(e.target.value)} className="input pl-9 py-2 text-sm w-full" />
            </div>
          </div>

          {loadingPendientes ? (
            <div className="flex justify-center py-12">
              <svg className="w-8 h-8 animate-spin text-slate-400" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            </div>
          ) : error ? (
            <div className="card p-6 text-center text-red-600">{error}</div>
          ) : filteredReservasPendientes.length === 0 ? (
            <div className="empty-state card py-16">
              <HiOutlineClipboardDocumentCheck className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500 font-medium">{searchTicketPendientes ? "No se encontraron reservas con ese ticket" : "No hay reservas pendientes"}</p>
              <p className="text-sm text-slate-400 mt-1">{searchTicketPendientes ? "Intenta con otro numero de ticket" : "Las nuevas solicitudes apareceran aqui"}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredReservasPendientes.map((reserva) => {
                const primer = getPrimerDiaItinerario(reserva.itinerario);
                return (
                  <div key={reserva.id ?? reserva.ticket} className="card-hover overflow-hidden">
                    <div className="flex">
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="text-base font-bold text-slate-900">Maquina {reserva.equipo}</p>
                            <p className="text-sm font-semibold text-brand-700 uppercase tracking-wide">{reserva.empresa}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="sm:hidden inline-flex items-center gap-1 bg-slate-900 text-white text-xs font-bold px-2.5 py-1 rounded-md">
                              <span className="opacity-70">Ticket</span> {reserva.ticket}
                            </span>
                            <span className={statusBadge(reserva.status)}>{reserva.status}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mt-3">
                          <div>
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Solicitado por</p>
                            <p className="text-sm text-slate-700">{reserva.nombre_agente || "\u2014"}</p>
                          </div>
                          {primer && (
                            <div>
                              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Primer dia</p>
                              <p className="text-sm text-slate-700">{formatFechaCortaMX(primer.fecha)} {primer.entrada?.slice(0, 5)} - {primer.salida?.slice(0, 5)}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Fecha solicitud</p>
                            <p className="text-sm text-slate-700">{formatFecha(reserva.fecha_reserva)}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Dias programados</p>
                            <p className="text-sm text-slate-700">{reserva.itinerario?.length ?? 0}</p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-slate-100">
                          <button onClick={() => handleOpenDrawer(reserva)} className="btn-primary text-xs py-1.5 px-3">
                            <HiOutlineInformationCircle className="w-4 h-4" /> Detalles
                          </button>
                          <button onClick={() => handleEditar(reserva.id, "pendientes")} className="btn-secondary text-xs py-1.5 px-3">
                            <HiOutlinePencilSquare className="w-4 h-4" /> Editar
                          </button>
                          <button onClick={() => handleOpenModal(reserva)} className="text-xs py-1.5 px-3 inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-btn font-semibold hover:bg-emerald-700 transition-colors">
                            <HiOutlineCheckCircle className="w-4 h-4" /> Aceptar
                          </button>
                          <button onClick={() => handleReject(reserva.id)} className="text-xs py-1.5 px-3 inline-flex items-center gap-1.5 bg-amber-500 text-white rounded-btn font-semibold hover:bg-amber-600 transition-colors">
                            <HiOutlineXCircle className="w-4 h-4" /> Rechazar
                          </button>
                          <button onClick={() => handleCancel(reserva.id, reserva)} className="btn-danger text-xs py-1.5 px-3">
                            <HiOutlineNoSymbol className="w-4 h-4" /> Cancelar
                          </button>
                        </div>
                      </div>

                      {/* Right accent strip */}
                      <div className="hidden sm:flex w-28 bg-slate-900 text-white flex-col items-center justify-center shrink-0">
                        <span className="text-[10px] font-bold uppercase tracking-widest opacity-70">Ticket</span>
                        <span className="text-3xl font-black leading-none mt-1">{reserva.ticket}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Apartados sin tecnico */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="section-title">Apartados sin Técnico</h2>
              <button onClick={fetchApartadosSinTecnico} disabled={loadingApartados} className="btn-secondary text-xs py-1.5 px-3">
                <HiOutlineArrowPath className={`w-4 h-4 ${loadingApartados ? "animate-spin" : ""}`} /> Actualizar
              </button>
            </div>
            {loadingApartados ? (
              <div className="flex justify-center py-8"><svg className="w-6 h-6 animate-spin text-slate-400" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg></div>
            ) : apartadosSinTecnico.length === 0 ? (
              <div className="card py-8 text-center">
                <p className="text-sm text-slate-500">No hay apartados pendientes de asignar técnico.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {apartadosSinTecnico.map((ap) => {
                  const primer = getPrimerDiaItinerario(ap.itinerario);
                  return (
                    <div key={ap.id} className="card-hover overflow-hidden">
                      <div className="flex">
                        <div className="flex-1 p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="text-base font-bold text-slate-900">{ap.empresa}</p>
                              <p className="text-xs text-slate-500 mt-0.5">Solicitado por: <span className="font-medium text-slate-700">{ap.nombre_agente}</span></p>
                            </div>
                            <span className="badge-blue">{ap.status}</span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                            {primer && (
                              <div><span className="font-semibold uppercase tracking-wider">Primer día: </span>{formatFechaCortaMX(primer.fecha)} {primer.entrada?.slice(0, 5)}</div>
                            )}
                            <div><span className="font-semibold uppercase tracking-wider">Días: </span>{ap.itinerario?.length ?? 0}</div>
                            <div><span className="font-semibold uppercase tracking-wider">Equipo/Maquina: </span>{ap.equipo || "\u2014"}</div>
                            <div><span className="font-semibold uppercase tracking-wider">Ciudad: </span>{ap.ciudad || "\u2014"}</div>
                            <div><span className="font-semibold uppercase tracking-wider">Localidad: </span>{ap.localidad || "\u2014"}</div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                            <button
                              onClick={() => { setSelectedApartado(ap); setTecnicoParaApartado(""); setOpenAsignarApartado(true); }}
                              className="text-xs py-1.5 px-3 inline-flex items-center gap-1.5 bg-blue-600 text-white rounded-btn font-semibold hover:bg-blue-700 transition-colors"
                            >
                              <HiOutlineCheckCircle className="w-4 h-4" /> Asignar Técnico
                            </button>
                            <button
                              onClick={() => handleEliminarApartado(ap)}
                              disabled={eliminandoApartadoId === ap.id}
                              className="btn-danger text-xs py-1.5 px-3"
                            >
                              <HiOutlineNoSymbol className="w-4 h-4" /> {eliminandoApartadoId === ap.id ? "Eliminando..." : "Eliminar"}
                            </button>
                          </div>
                        </div>
                        <div className="hidden sm:flex w-20 bg-blue-700 text-white flex-col items-center justify-center shrink-0">
                          <span className="text-[10px] font-bold uppercase tracking-widest opacity-70">Sin</span>
                          <span className="text-xs font-black leading-none mt-1">Técnico</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="xl:col-span-1">
          <div className="card p-5 sticky top-6">
            <h3 className="font-semibold text-slate-900 mb-1">Solicitudes Aprobadas</h3>
            <p className="text-xs text-slate-500 mb-4">Ultimas solicitudes activas y extendidas</p>

            <div className="relative mb-3">
              <HiOutlineMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" placeholder="Buscar por ticket..." value={searchTicket} onChange={(e) => setSearchTicket(e.target.value)} className="input pl-9 py-2 text-sm" />
            </div>

            <button onClick={fetchReservasActivas} disabled={loadingActivas} className="btn-primary w-full mb-4 text-sm">
              <HiOutlineArrowPath className={`w-4 h-4 ${loadingActivas ? "animate-spin" : ""}`} />
              Actualizar
            </button>

            {loading || loadingActivas || tecnicos.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Cargando...</p>
            ) : filteredReservasActivas.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No hay solicitudes aprobadas.</p>
            ) : (
              <div className="space-y-3">
                {filteredReservasActivas.slice(0, 5).map((reserva) => {
                  const tec = tecnicos.find((t) => t.correo_tecnico === (reserva.correo_tecnico || "").trim());
                  return (
                    <div key={reserva.id} className="rounded-lg border border-slate-100 p-3 hover:border-slate-200 transition-colors">
                      <div className="flex items-start justify-between mb-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">{reserva.empresa}</p>
                        <span className={`${statusBadge(reserva.status)} text-[10px]`}>{reserva.status}</span>
                      </div>
                      <div className="space-y-0.5 text-xs text-slate-500">
                        <p>Ticket: <span className="text-slate-700 font-medium">{reserva.ticket}</span></p>
                        <p>Maquina: <span className="text-slate-700">{reserva.equipo}</span></p>
                        <p>Tecnico: <span className="text-slate-700">{tec?.nombre_tecnico || "No asignado"}</span></p>
                      </div>
                      <div className="flex gap-1.5 mt-2">
                        <button onClick={() => handleEditar(reserva.id, "aprobados")} className="btn-ghost text-xs py-1 px-2">Editar</button>
                        <button onClick={() => handleOpenDrawer(reserva)} className="btn-ghost text-xs py-1 px-2">Detalles</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Technician Assignment Modal */}
      {open && (
        <>
          <div className="overlay" onClick={handleCloseModal} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="modal w-full max-w-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Asignar Tecnico</h3>
                <button onClick={handleCloseModal} className="btn-icon"><HiOutlineXMark className="w-5 h-5" /></button>
              </div>

              <div className="space-y-4">
                <div>
                  <SearchableUserSelect
                    users={tecnicos}
                    value={selectedTecnico}
                    onChange={(id) => {
                      if (!id) {
                        setSelectedTecnico("")
                        return
                      }
                      setSelectedTecnico(tecnicos.find((tec) => tec.id === id)?.correo_tecnico || "")
                    }}
                    label="Tecnico"
                    placeholder="Buscar tecnico..."
                    required
                  />
                </div>

                <div>
                  <label className="label">Tipo de servicio</label>
                  <select value={selectedTipoServicio} onChange={(e) => setSelectedTipoServicio(e.target.value)} className="input">
                    <option value="">-- Selecciona tipo --</option>
                    <option value="Instalación y Capacitación">Instalación y Capacitación</option>
                    <option value="Mantenimiento Correctivo">Mantenimiento Correctivo</option>
                    <option value="Servicio Pagado">Servicio Pagado</option>
                    <option value="Servicio en Garantía">Servicio en Garantía</option>
                  </select>
                </div>

                <div>
                  <label className="label">Plantilla</label>
                  <select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)} className="input">
                    <option value="">-- Selecciona plantilla --</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} - ({t.model_name})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button onClick={handleCloseModal} disabled={loadingAsignar} className="btn-secondary">Cancelar</button>
                <button onClick={handleAccept} disabled={loadingAsignar} className="btn-primary">
                  {loadingAsignar ? (
                    <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  ) : "Agendar"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Asignar tecnico a apartado modal */}
      {openAsignarApartado && (
        <>
          <div className="overlay" onClick={() => setOpenAsignarApartado(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="modal w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Asignar Técnico</h3>
                <button onClick={() => setOpenAsignarApartado(false)} className="btn-icon"><HiOutlineXMark className="w-5 h-5" /></button>
              </div>
              {selectedApartado && (
                <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm">
                  <p className="font-semibold text-slate-800">{selectedApartado.empresa}</p>
                  <p className="text-slate-500 text-xs mt-0.5">Solicitado por: {selectedApartado.nombre_agente}</p>
                  <p className="text-slate-500 text-xs">Equipo/Maquina: {selectedApartado.equipo || "\u2014"}</p>
                  <p className="text-slate-500 text-xs">Ciudad: {selectedApartado.ciudad || "\u2014"} ({selectedApartado.localidad || "\u2014"})</p>
                  <p className="text-slate-500 text-xs">Días: {selectedApartado.itinerario?.length ?? 0}</p>
                  {selectedApartado.itinerario?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200 space-y-1.5">
                      {[...selectedApartado.itinerario]
                        .sort((a, b) => toLocalDateFromUTCDateOnly(a.fecha) - toLocalDateFromUTCDateOnly(b.fecha))
                        .map((it, i) => (
                          <div key={i} className="flex items-center justify-between text-xs rounded-md bg-white border border-slate-200 px-3 py-1.5">
                            <span className="font-medium text-slate-700">{formatFechaCortaMX(it.fecha)}</span>
                            <span className="text-slate-500">{it.entrada?.slice(0, 5)} — {it.salida?.slice(0, 5)}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
              <SearchableUserSelect
                users={tecnicos}
                value={tecnicoParaApartado}
                onChange={(id) => setTecnicoParaApartado(tecnicos.find((tec) => tec.id === id)?.correo_tecnico || "")}
                label="Técnico"
                placeholder="Buscar técnico..."
                required
              />
              <p className="mt-3 text-xs text-slate-500">La reserva permanecerá en estado <strong>Apartado</strong> hasta que el solicitante complete los datos del ticket.</p>
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={() => setOpenAsignarApartado(false)} disabled={loadingAsignarApartado} className="btn-secondary">Cancelar</button>
                <button onClick={handleAsignarTecnicoApartado} disabled={loadingAsignarApartado} className="btn-primary">
                  {loadingAsignarApartado ? <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> : "Asignar"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Detail Drawer */}
      {drawerOpen && (
        <>
          <div className="overlay" onClick={handleCloseDrawer} />
          <aside className="fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white shadow-modal overflow-y-auto">
            {selectedReserva && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-slate-900">{selectedReserva.empresa}</h2>
                  <button onClick={handleCloseDrawer} className="btn-icon"><HiOutlineXMark className="w-5 h-5" /></button>
                </div>
                <div className="space-y-4">
                  {[
                    { label: "Cliente", value: selectedReserva.contacto },
                    { label: "Maquina", value: selectedReserva.equipo },
                    { label: "Solicitado por", value: selectedReserva.nombre_agente },
                    { label: "Tecnico", value: tecnicoEncontrado?.nombre_tecnico || "No asignado" },
                    { label: "Localidad", value: selectedReserva.localidad },
                  ].map((item) => (
                    <div key={item.label} className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">{item.label}</span>
                      <span className="text-sm font-medium text-slate-800">{item.value}</span>
                    </div>
                  ))}

                  <div className="flex items-start gap-2 py-2 border-b border-slate-100">
                    <HiOutlineMapPin className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-slate-500 mb-0.5">Ubicacion</p>
                      <p className="text-sm font-medium text-slate-800">{selectedReserva.domicilio}</p>
                      {selectedReserva.domicilio && (
                        <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
                          <iframe
                            title={`mapa-reserva-${selectedReserva.id || "sin-id"}`}
                            src={getGoogleMapsEmbedUrl(selectedReserva.domicilio)}
                            className="w-full border-0"
                            style={{ minHeight: "220px" }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 py-2 border-b border-slate-100">
                    <HiOutlineCheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                    <div>
                      <p className="text-xs text-slate-500 mb-0.5">Estatus</p>
                      <span className={statusBadge(selectedReserva.status)}>{selectedReserva.status}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="section-title mb-3">Actividades</h3>
                  {selectedReserva.actividades?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedReserva.actividades.map((act, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
                          <HiOutlineChatBubbleLeft className="w-4 h-4 text-slate-400 shrink-0" />
                          <span className="capitalize">{act}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">No hay actividades registradas</p>
                  )}
                </div>

                <div className="mt-6">
                  <h3 className="section-title mb-3">Itinerario</h3>
                  {selectedReserva.itinerario?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedReserva.itinerario.map((it, i) => (
                        <div key={i} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg text-sm">
                          <span className="font-medium text-slate-800">{formatFecha(it.fecha)}</span>
                          <span className="text-slate-500">{formatHora(it.entrada)} -- {formatHora(it.salida)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">No hay itinerario registrado</p>
                  )}
                </div>
              </div>
            )}
          </aside>
        </>
      )}
    </div>
  );
};

export default AgendarServicio;
